import type {
  EditableGridCell,
  EditListItem,
  Item,
} from "@glideapps/glide-data-grid";
import { toast } from "@volca/ui/lib/toast";
import {
  getCellKind,
  getRawInputKind,
  isFormulaCell,
} from "@/payroll/ai-script/cell-kind-utils";
import { aiScriptSpreadsheetSheetKey } from "@/payroll/ai-script/query";
import {
  buildOptimisticCell,
  findSheetResponseCell,
  patchSpreadsheetSheetCells,
} from "@/payroll/ai-script/spreadsheet-cache";
import type {
  AiSpreadsheetValueType,
  IAiSpreadsheetCell,
  IAiSpreadsheetSheetResponse,
} from "@/payroll/ai-script/types";
import {
  type AdaptedSpreadsheetSheet,
  getCellFormatType,
  getInCellEditValue,
  isCellEditable,
} from "./adapter";
import { getCellEditNotification } from "./cell-edit-feedback";
import type { FormulaBarCommitTarget } from "./formula-bar";
import type { ManualRowActions } from "./manual-row-actions";
import type {
  ManualRowEditBuffer,
  ViewerEditContext,
} from "./manual-row-edit-buffer";
import type { MutationErrorHandlers } from "./mutation-error-feedback";
import { getEditedRawInput } from "./utils";
import {
  buildCellEditEntry,
  buildFormatEditEntry,
} from "./workbook-history-ops";
import type { WorkbookHistoryEntry } from "./workbook-history-types";

interface CreateCellEditHandlersParams extends ViewerEditContext {
  adaptedSheet: AdaptedSpreadsheetSheet | null;
  canMutateActiveSheet: boolean;
  findLoadedCellById: (cellId: string) => IAiSpreadsheetCell | undefined;
  coordinateForCell: (cell: IAiSpreadsheetCell) => string;
  showMutationErrorFeedback: MutationErrorHandlers["showMutationErrorFeedback"];
  buffer: Pick<ManualRowEditBuffer, "bufferManualRowEdit">;
  rowActions: Pick<ManualRowActions, "appendManualRow">;
}

export const createCellEditHandlers = ({
  businessId,
  runId,
  queryClient,
  history,
  mutations: {
    updateCellAsync,
    directUpdateCellAsync,
    cellEditFeedbackCollectorRef,
  },
  activeSheet,
  recordWorkbookVersion,
  handleMutationError,
  adaptedSheet,
  canMutateActiveSheet,
  findLoadedCellById,
  coordinateForCell,
  showMutationErrorFeedback,
  buffer: { bufferManualRowEdit },
  rowActions: { appendManualRow },
}: CreateCellEditHandlersParams) => {
  const commitRawInput = (cellId: string, rawInput: string) => {
    if (!activeSheet) return;

    const sheetKey = aiScriptSpreadsheetSheetKey(
      businessId,
      runId,
      activeSheet.id,
    );
    const cachedSheet =
      queryClient.getQueryData<IAiSpreadsheetSheetResponse>(sheetKey);
    const priorCell =
      findSheetResponseCell(cachedSheet, cellId) ?? findLoadedCellById(cellId);
    const nextKind = getRawInputKind(rawInput);

    if (
      priorCell &&
      bufferManualRowEdit(priorCell, { raw_input: rawInput, kind: nextKind })
    ) {
      return;
    }

    const priorKind = getCellKind(priorCell);
    const wasFormula = priorCell ? isFormulaCell(priorCell) : false;

    const pendingEntry: WorkbookHistoryEntry | undefined = priorCell
      ? buildCellEditEntry({
          sheetId: activeSheet.id,
          cellId,
          coordinate: coordinateForCell(priorCell),
          priorRawInput: priorCell.raw_input ?? "",
          nextRawInput: rawInput,
          priorKind,
          nextKind,
        })
      : undefined;

    if (priorCell) {
      queryClient.setQueryData<IAiSpreadsheetSheetResponse>(sheetKey, (data) =>
        patchSpreadsheetSheetCells(data, [
          buildOptimisticCell(priorCell, rawInput, undefined, nextKind),
        ]),
      );
    }

    void updateCellAsync({
      runId,
      sheetId: activeSheet.id,
      cellId,
      rawInput,
      kind: nextKind,
    })
      .then((response) => {
        recordWorkbookVersion(response.operation.version);
        if (pendingEntry) history.push(pendingEntry);
        const refreshedCell = findSheetResponseCell(
          queryClient.getQueryData<IAiSpreadsheetSheetResponse>(sheetKey),
          cellId,
        );
        const notification = getCellEditNotification({
          cell: refreshedCell,
          wasFormula,
        });
        const feedback = cellEditFeedbackCollectorRef.current.completeSuccess(
          response.batch,
          notification,
        );
        if (feedback) {
          toast.info(feedback.title, {
            description: feedback.description,
            id: feedback.id,
          });
        }
      })
      .catch((mutationError: unknown) => {
        const feedback = cellEditFeedbackCollectorRef.current.completeError(
          mutationError,
          "Failed to update cell",
        );
        if (feedback) showMutationErrorFeedback(feedback);
      });
  };

  const formatCell = (
    cellRecord: IAiSpreadsheetCell,
    valueType: AiSpreadsheetValueType,
  ) => {
    if (!activeSheet || !canMutateActiveSheet || cellRecord.locked) return;

    const priorValueType = getCellFormatType(cellRecord);
    const pendingEntry: WorkbookHistoryEntry | undefined =
      priorValueType !== valueType
        ? buildFormatEditEntry({
            sheetId: activeSheet.id,
            cellId: cellRecord.id,
            coordinate: coordinateForCell(cellRecord),
            priorValueType,
            nextValueType: valueType,
          })
        : undefined;

    void directUpdateCellAsync({
      runId,
      sheetId: activeSheet.id,
      cellId: cellRecord.id,
      rawInput: cellRecord.raw_input ?? "",
      valueType,
    })
      .then((response) => {
        recordWorkbookVersion(response.version);
        if (pendingEntry) history.push(pendingEntry);
      })
      .catch((mutationError: unknown) =>
        handleMutationError("Failed to format cell", mutationError),
      );
  };

  const commitCellEdit = (cell: Item, newValue: EditableGridCell) => {
    if (!adaptedSheet || !canMutateActiveSheet) return;

    const cellRecord = adaptedSheet.getCellRecord(cell[0], cell[1]);

    if (!cellRecord) {
      if (adaptedSheet.isAddRowCell(cell[0], cell[1])) {
        const typed = getEditedRawInput(newValue);
        if (typed.trim() !== "") {
          appendManualRow({ col: cell[0] + 1, value: typed });
        }
        return;
      }
      toast.error("Only existing Grid cells can be edited right now.");
      return;
    }

    if (!isCellEditable(cellRecord)) return;

    const rawInput = getEditedRawInput(newValue);

    if (rawInput === getInCellEditValue(cellRecord)) return;

    commitRawInput(cellRecord.id, rawInput);
  };

  const handleCellsEdited = (edits: readonly EditListItem[]) => {
    if (!adaptedSheet || !canMutateActiveSheet) return true;

    const addRowSeeds: { col: number; value: string }[] = [];
    edits.forEach(({ location, value }) => {
      if (adaptedSheet.getCellRecord(location[0], location[1])) {
        commitCellEdit(location, value);
      } else if (adaptedSheet.isAddRowCell(location[0], location[1])) {
        const typed = getEditedRawInput(value);
        if (typed.trim() !== "") {
          addRowSeeds.push({ col: location[0] + 1, value: typed });
        }
      }
    });
    if (addRowSeeds.length > 0) appendManualRow(addRowSeeds);
    return true;
  };

  const handleFormulaBarCommit = (
    rawInput: string,
    target: FormulaBarCommitTarget,
  ) => {
    if (!canMutateActiveSheet) return;

    if (target.kind === "new-row") {
      if (rawInput.trim() !== "") {
        appendManualRow({ col: target.columnIndex + 1, value: rawInput });
      }
      return;
    }

    const targetCell = findLoadedCellById(target.cellId);
    if (!targetCell || !isCellEditable(targetCell)) return;
    commitRawInput(targetCell.id, rawInput);
  };

  return {
    commitRawInput,
    formatCell,
    commitCellEdit,
    handleCellsEdited,
    handleFormulaBarCommit,
  };
};

export type CellEditHandlers = ReturnType<typeof createCellEditHandlers>;
